<template>
  <div class="product-page">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Đang tải dữ liệu...</p>
    </div>
    <!-- Header -->
    <div class="product-header">
      <h1>Quản lý Đơn hàng</h1>
      <div class="button-action">
        <button @click="viewOrderCreateModal" class="btn-add">
          + Thêm đơn hàng
        </button>
        <div class="action">
          <button @click="triggerFileInput" class="btn-add">
            + Nhập đơn hàng
          </button>
          <input
            type="file"
            ref="fileInput"
            style="display: none"
            accept=".xlsx, .xls, .csv"
            @change="handleFileImport"
          />
        </div>
      </div>
    </div>

    <!-- Filter -->
    <div class="product-filter">
      <input v-model="search" type="text" placeholder="Tìm kiếm hoá đơn..." />
   
      <button @click="fetchInvoices()" class="btn-search">Tìm kiếm</button>
      <div class="column-toggle">
        <button @click="showColumnPanel = !showColumnPanel">
          ⚙️ Cột hiển thị
        </button>

        <div v-if="showColumnPanel" class="column-panel">
          <label v-for="col in columns" :key="col.key">
            <input type="checkbox" v-model="col.visible" />
            {{ col.label }}
          </label>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="product-table">
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th v-for="col in visibleColumns" :key="col.key">
              {{ col.label }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="(item, index) in invoices"
            :key="item.id"
            @click="viewInvoiceDetail(item)"
            style="cursor: pointer"
          >
            <td>{{ index + 1 }}</td>
            <td v-for="col in visibleColumns" :key="col.key">
              {{ renderValue(item, col) }}
            </td>
          </tr>
          <tr v-if="!invoices.length">
            <td colspan="100%" class="empty">Không có dữ liệu</td>
          </tr>
        </tbody>
        <tfoot v-if="invoices.length">
          <tr style="background-color: #f9f9f9; font-weight: bold">
            <td :colspan="getColumnIndex('tong_tien_hang') + 1">Tổng cộng:</td>
            <td style="color: red">{{ formatCurrency(totalAmount) }}</td>
            <td style="color: blue">{{ formatCurrency(totalDiscount) }}</td>
            <td
              :colspan="
                visibleColumns.length - getColumnIndex('tong_tien_hang')
              "
            ></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.total > 0" class="pagination">
      <div class="pagination-info">
        Hiển thị {{ pagination.from }} - {{ pagination.to }} trong tổng số
        {{ pagination.total }} hoá đơn
      </div>
      <div class="pagination-controls">
        <button
          @click="changePage(pagination.current_page - 1)"
          :disabled="pagination.current_page === 1"
          class="btn btn-outline btn-sm"
        >
          Trước
        </button>

        <button
          v-for="page in visiblePages"
          :key="page"
          @click="changePage(page)"
          :class="[
            'btn',
            'btn-sm',
            page === pagination.current_page ? 'btn-primary' : 'btn-outline',
          ]"
        >
          {{ page }}
        </button>

        <button
          @click="changePage(pagination.current_page + 1)"
          :disabled="pagination.current_page === pagination.last_page"
          class="btn btn-outline btn-sm"
        >
          Sau
        </button>
      </div>
    </div>

    <!-- Modal -->
    <InvoiceDetailModal
      v-if="showInvoiceDetailModal"
      :invoice="selectedInvoice"
      @close="showInvoiceDetailModal = false"
    />

    <OrderCreateModal
      v-if="showOrderCreateModal"
      @close="showOrderCreateModal = false"
      @created="createInvoiceSuccess"
    />
  </div>
</template>

<script setup>
import InvoiceDetailModal from "@/components/modal/invoices/InvoiceDetailModal.vue";
import OrderCreateModal from "@/components/modal/invoices/OrderCreateModal.vue";
import { usePagination } from "@/composables/usePagination";
import { useToast } from "@/composables/useToast";
import { importApi, invoiceApi } from "@/services/api";
import { formatCurrency, formatDate } from "@/utils/helpers";
import { computed, onMounted, ref } from "vue";

const loading = ref(false);
const fileInput = ref(null);
const toast = useToast();
const search = ref("");
const invoices = ref([]);
const showInvoiceDetailModal = ref(false);
const selectedInvoice = ref(null);
const showColumnPanel = ref(false);
const showOrderCreateModal = ref(false);
const totalAmount = ref(0);
const totalDiscount = ref(0);

const columns = ref([
  { key: "chi_nhanh", label: "Chi nhánh", visible: false },
  { key: "ma_hoa_don", label: "Mã hoá đơn", visible: true },
  { key: "thoi_gian", label: "Thời gian", visible: false, type: "date" },
  { key: "thoi_gian_tao", label: "Thời gian tạo", visible: false },
  { key: "ngay_cap_nhat", label: "Ngày cập nhật", visible: false },
  { key: "ma_dat_hang", label: "Mã đặt hàng", visible: false },
  { key: "ma_ycsc", label: "Mã yêu cầu sửa chữa", visible: false },
  { key: "ma_khach_hang", label: "Mã khách hàng", visible: true },
  { key: "ten_khach_hang", label: "Tên khách hàng", visible: true },
  { key: "email", label: "Email", visible: false },
  { key: "dien_thoai", label: "Điện thoại", visible: true },
  { key: "dia_chi", label: "Địa chỉ", visible: false },
  { key: "khu_vuc", label: "Khu vực", visible: false },
  { key: "phuong_xa", label: "Phường xã", visible: false },
  { key: "ngay_sinh", label: "Ngày sinh", visible: false },
  { key: "bang_gia", label: "Bảng giá", visible: false },
  { key: "nguoi_ban", label: "Người bán", visible: false },
  { key: "kenh_ban", label: "Kênh bán", visible: false },
  { key: "nguoi_tao", label: "Người tạo", visible: false },
  { key: "ghi_chu", label: "Ghi chú", visible: false },
  {
    key: "tong_tien_hang",
    label: "Tổng tiền hàng",
    visible: true,
    type: "currency",
  },
  {
    key: "giam_gia_hoa_don",
    label: "Giảm giá hoá đơn",
    visible: true,
    type: "currency",
  },
  { key: "thu_khac", label: "Thu khách", visible: false },
  {
    key: "khach_can_tra",
    label: "Khách cần trả",
    visible: false,
    type: "currency",
  },
  {
    key: "khach_da_tra",
    label: "Khách đã trả",
    visible: false,
    type: "currency",
  },
  { key: "tien_mat", label: "Tiền mặt", visible: false },
  { key: "the", label: "Thẻ", visible: false },
  { key: "vi", label: "Ví", visible: false },
  { key: "chuyen_khoan", label: "Chuyển khoản", visible: false },
  { key: "voucher", label: "Voucher", visible: false },
  { key: "ma_voucher", label: "Mã Voucher", visible: false },
  { key: "con_can_thu_cod", label: "Thu COD", visible: false },
  { key: "trang_thai", label: "Trạng thái", visible: false },
]);
const renderValue = (item, col) => {
  const value = item[col.key];
  if (col.type === "currency") {
    return formatCurrency(value);
  }
  if(col.type === "date"){
    return formatDate(value);
  }

  return value || "-";
};

const visibleColumns = computed(() =>
  columns.value.filter((col) => col.visible),
);

const getColumnIndex = (columnKey) => {
  return visibleColumns.value.findIndex((col) => col.key === columnKey);
};

const triggerFileInput = () => {
  fileInput.value.click();
};
const handleFileImport = async (event) => {
  const file = event.target.files[0];
  if (!file) return;
  const fileName = file.name.toLowerCase();
  if (
    !fileName.endsWith(".xlsx") &&
    !fileName.endsWith(".xls") &&
    !fileName.endsWith(".csv")
  ) {
    toast.error("Vui lòng chọn file Excel (.xlsx, .xls) hoặc .csv");
    return;
  }

  const formData = new FormData();
  formData.append("file", file);

  loading.value = true;
  try {
    const response = await importApi.importInvoices(formData);
    toast.success(
      `Đã nhập thành công ${response.data.imported_count}} sản phẩm`,
    );
    event.target.value = "";
    fetchInvoices();
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || "Có lỗi xảy ra khi nhập hoá đơn";
    toast.error(errorMessage);
    event.target.value = "";
  } finally {
    loading.value = false;
  }
};

const fetchInvoices = async () => {
  loading.value = true;
  try {
    const response = await invoiceApi.getInvoices({
      page: pagination.current_page,
      search: search.value,
      per_page: pagination.per_page || 15,
    });
    invoices.value = response.data.data.data || response.data;
    const { ...paginationMeta } = response.data.data;
    updatePagination(paginationMeta);
    totalAmount.value = response.data.total_all_amount;
    totalDiscount.value = response.data.total_discount;
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || "Có lỗi xảy ra khi nhập sản phẩm";
    toast.error(errorMessage);
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const viewInvoiceDetail = (invoice) => {
  selectedInvoice.value = invoice;
  showInvoiceDetailModal.value = true;
};

const viewOrderCreateModal = () => {
  showOrderCreateModal.value = true;
};

const createInvoiceSuccess = () => {
  showOrderCreateModal.value = false;
  fetchInvoices();
};
const { pagination, visiblePages, changePage, updatePagination } =
  usePagination(fetchInvoices);
onMounted(fetchInvoices);
</script>

<style scoped>
/* ===== PAGE ===== */
.product-page {
  background: #f5f6fa;
  padding: 20px;
  border-radius: 12px;
}

/* ===== HEADER ===== */
.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.product-header h1 {
  font-size: 22px;
  font-weight: 700;
}

.btn-add {
  background: linear-gradient(135deg, #f36c21, #ffd54f);
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 20px;
  cursor: pointer;
  transition: 0.3s;
}

.btn-add:hover {
  transform: scale(1.05);
}

/* ===== FILTER ===== */
.product-filter {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.product-filter input,
.product-filter select {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ddd;
  outline: none;
}

.btn-search {
  background: #333;
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 8px;
}

/* ===== TABLE ===== */
.product-table {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: #111;
  color: white;
}

th,
td {
  padding: 12px 14px;
  text-align: left;
  font-size: 14px;
}

tbody tr {
  border-bottom: 1px solid #eee;
}

tbody tr:hover {
  background: #f9f9f9;
}

/* ===== ACTION BUTTON ===== */
.btn-edit {
  background: #2196f3;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 6px;
  margin-right: 5px;
}

.btn-delete {
  background: #f44336;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 6px;
}
</style>
